/*
 * Copyright (C) 2019 Frontier Developments PLC.
 */

import {Vue} from 'vue-property-decorator';
import Vuex, {ModuleTree, MutationPayload, Store} from 'vuex';
import {RootState} from './types';
import {userSessionModule} from '@/store/modules/user-session';
import {settingsModule} from '@/store/modules/settings';
import {formStateModule} from '@/store/modules/form';
import {gamesStateModule} from '@/store/modules/games';
import {issuesStateModule} from '@/store/modules/issues';
import {searchStateModule} from '@/store/modules/search';
import {messageStateModule} from '@/store/modules/message';
import {voteStateModule} from '@/store/modules/vote';
import {contentStateModule} from '@/store/modules/content';
import {customerModule} from '@/store/modules/customer';

Vue.use(Vuex);

/**
 * Vuex entry point.
 *
 * @author Jonny Cook <jcook@frontier.co.uk>
 */

/**
 * The user session store module.
 *
 * This module is imported by vuex as a representation of the user session.
 *
 * @author Jonny Cook <jcook@frontier.co.uk>
 */
export const state: RootState = {
    version: '1.0.3',
    build: process.env.VUE_APP_COMMIT || 'development',
};

const modules: ModuleTree<RootState> = {
    userSessionModule,
    settingsModule,
    formStateModule,
    gamesStateModule,
    issuesStateModule,
    searchStateModule,
    messageStateModule,
    voteStateModule,
    contentStateModule,
    customerModule,
};

const store: Store<RootState> = new Vuex.Store<RootState>({
    state,
    modules,
});

// subscribe store
store.subscribe((mutation: MutationPayload, rootState: any) => {
    // check mutation type format
    if (!mutation.type.match(/(.+module)\/(.+)/i)) {
        return;
    }

    // Get module and action
    const module: string = mutation.type.replace(/(.+module)\/(.+)/i, '$1');
    const action: string = mutation.type.replace(/(.+module)\/(.+)/i, '$2');

    // check for mutation support
    switch (module) {
      case 'userSessionModule':
        if (!['authenticate', 'userDetails', 'logout', 'updateCurrentView'].includes(action)) {
          return;
        }
        break;
      case 'settingsModule':
        if (!['updateLanguage', 'updateConsent'].includes(action)) {
          return;
        }
        break;
      case 'formStateModule':
        break;
      default:
        return;
    }

    // update state
    localStorage.setItem(`issues/store/${module}/version`, rootState.version);
    localStorage.setItem(`issues/store/${module}`, JSON.stringify(rootState[module]));
  });

// load in modules that exist in local storage
for (const module of Object.keys(modules)) {
    if (!module.match(/^.+module$/i)) {
      continue;
    }
    const version: string = localStorage.getItem(`issues/store/${module}/version`) || '';
    if (version !== state.version) {
      continue;
    }

    const moduleState: string = localStorage.getItem(`issues/store/${module}`) || '';
    if (moduleState.length > 0) {
      Object.assign(modules[module]['state'], JSON.parse(moduleState));
    }
  }

export default store;

